import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { BrokerAccount } from '../types/portfolio';

export interface AddableStock {
  id: number;
  symbol: string;
  name: string;
  isin: string;
}

interface AddStockToPortfolioModalProps {
  show: boolean;
  stock?: AddableStock | null;
  searchable?: boolean;
  onHide: () => void;
  onSuccess?: (stock: AddableStock) => void | Promise<void>;
}

const today = () => new Date().toISOString().split('T')[0];

const formatCurrency = (amount: number) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2
}).format(Number.isFinite(amount) ? amount : 0);

const AddStockToPortfolioModal: React.FC<AddStockToPortfolioModalProps> = ({
  show,
  stock,
  searchable = false,
  onHide,
  onSuccess
}) => {
  const { token } = useAuth();
  const [selectedStock, setSelectedStock] = useState<AddableStock | null>(stock || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AddableStock[]>([]);
  const [searching, setSearching] = useState(false);
  const [addInitialTransaction, setAddInitialTransaction] = useState(true);
  const [transaction, setTransaction] = useState({
    date: today(),
    txn_type: 'buy',
    quantity: '',
    price: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState<BrokerAccount[]>([]);
  const [brokerAccountId, setBrokerAccountId] = useState('');
  const [accountsLoading, setAccountsLoading] = useState(false);

  useEffect(() => {
    if (!show) return;
    setSelectedStock(stock || null);
    setSearchQuery('');
    setSearchResults([]);
    setAddInitialTransaction(true);
    setTransaction({
      date: today(),
      txn_type: 'buy',
      quantity: '',
      price: '',
      notes: ''
    });
    setError('');
    setBrokerAccountId('');
    setAccountsLoading(true);
    axios.get(`${API_BASE_URL}/broker-accounts/`, {
      headers: { Authorization: `Token ${token || localStorage.getItem('token') || ''}` }
    }).then((response) => {
      setAccounts(Array.isArray(response.data) ? response.data : []);
    }).catch(() => {
      setAccounts([]);
    }).finally(() => setAccountsLoading(false));
  }, [show, stock, token]);

  const handleSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (!query) {
      setError('Enter a symbol, name, or ISIN to search.');
      return;
    }

    setSearching(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/stocks/`, {
        params: { search: query, page_size: 50 },
        headers: { Authorization: `Token ${token || localStorage.getItem('token') || ''}` }
      });
      const results = (response.data.results || []) as AddableStock[];
      setSearchResults(results);
      setSelectedStock(results[0] || null);
      if (results.length === 0) setError('No stocks found for that search.');
    } catch (err: any) {
      console.error('Stock search error:', err);
      setError(err?.response?.data?.error || 'Failed to search stocks.');
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedStock) {
      setError('Select a stock before adding it to your portfolio.');
      return;
    }

    const quantity = Number(transaction.quantity);
    const price = Number(transaction.price);
    if (addInitialTransaction && (!Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(price) || price <= 0)) {
      setError('Enter a valid quantity and price for the initial transaction.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {
        stock_id: selectedStock.id,
        notes: transaction.notes
      };
      if (brokerAccountId) payload.broker_account_id = Number(brokerAccountId);
      if (addInitialTransaction) {
        payload.date = transaction.date;
        payload.txn_type = transaction.txn_type;
        payload.quantity = quantity;
        payload.price = price;
      }

      await axios.post(`${API_BASE_URL}/stocks/add/`, payload, {
        headers: { Authorization: `Token ${token || localStorage.getItem('token') || ''}` }
      });
      await onSuccess?.(selectedStock);
      onHide();
    } catch (err: any) {
      console.error('Add stock to portfolio error:', err);
      setError(err?.response?.data?.error || err?.response?.data?.detail || 'Failed to add stock to portfolio.');
    } finally {
      setSaving(false);
    }
  };

  const totalAmount = Number(transaction.quantity) * Number(transaction.price);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {selectedStock ? `Add ${selectedStock.symbol} to Portfolio` : 'Add Stock to Portfolio'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

        {searchable && (
          <Form onSubmit={handleSearch} className="mb-3">
            <Form.Group>
              <Form.Label>Find a stock</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="search"
                  placeholder="Search by symbol, name, or ISIN"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
                <Button type="submit" variant="outline-primary" disabled={searching}>
                  {searching ? <Spinner animation="border" size="sm" /> : 'Search'}
                </Button>
              </div>
            </Form.Group>
          </Form>
        )}

        {searchable && searchResults.length > 0 && (
          <Form.Group className="mb-3">
            <Form.Label>Select stock</Form.Label>
            <Form.Select
              aria-label="Select stock"
              value={selectedStock?.id || ''}
              onChange={(event) => setSelectedStock(searchResults.find((item) => item.id === Number(event.target.value)) || null)}
            >
              {searchResults.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.symbol} - {item.name} ({item.isin})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {selectedStock && (
          <Alert variant="light" className="border mb-3">
            <strong>{selectedStock.symbol}</strong> · {selectedStock.name}
            <div className="small text-muted">ISIN: {selectedStock.isin || 'Not available'}</div>
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Holding account</Form.Label>
            <Form.Select
              aria-label="Holding account"
              value={brokerAccountId}
              disabled={accountsLoading}
              onChange={(event) => setBrokerAccountId(event.target.value)}
            >
              <option value="">Manual / Unassigned</option>
              {accounts
                .filter((account) => !['manual', 'legacy'].includes(account.provider))
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.display_name} ({account.provider_label})
                  </option>
                ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Choose where these shares are held. Manual / Unassigned is safe when no broker statement exists yet.
            </Form.Text>
          </Form.Group>

          <Form.Check
            type="switch"
            id="add-initial-stock-transaction"
            className="mb-3"
            label="Add an initial transaction now"
            checked={addInitialTransaction}
            onChange={(event) => setAddInitialTransaction(event.target.checked)}
          />

          {addInitialTransaction && (
            <>
              <div className="row g-3">
                <Form.Group className="col-md-6">
                  <Form.Label>Transaction date</Form.Label>
                  <Form.Control
                    type="date"
                    value={transaction.date}
                    onChange={(event) => setTransaction((previous) => ({ ...previous, date: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group className="col-md-6">
                  <Form.Label>Transaction type</Form.Label>
                  <Form.Select
                    value={transaction.txn_type}
                    onChange={(event) => setTransaction((previous) => ({ ...previous, txn_type: event.target.value }))}
                  >
                    <option value="buy">Buy</option>
                    <option value="sell">Sell</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="col-md-6">
                  <Form.Label>Quantity</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Enter number of shares"
                    value={transaction.quantity}
                    onChange={(event) => setTransaction((previous) => ({ ...previous, quantity: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group className="col-md-6">
                  <Form.Label>Price per share (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Enter price per share"
                    value={transaction.price}
                    onChange={(event) => setTransaction((previous) => ({ ...previous, price: event.target.value }))}
                  />
                </Form.Group>
              </div>
              {Number.isFinite(totalAmount) && totalAmount > 0 && (
                <Alert variant="info" className="mt-3 mb-3">
                  <strong>Total amount:</strong> {formatCurrency(totalAmount)}
                </Alert>
              )}
            </>
          )}

          <Form.Group className="mt-3">
            <Form.Label>Notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Add any notes..."
              value={transaction.notes}
              onChange={(event) => setTransaction((previous) => ({ ...previous, notes: event.target.value }))}
            />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button type="button" variant="secondary" onClick={onHide}>Cancel</Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving || !selectedStock}
            >
              {saving ? <><Spinner animation="border" size="sm" className="me-2" />Adding...</> : 'Add to Portfolio'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddStockToPortfolioModal;
